import { Button, Separator, Skeleton, Tooltip } from "@/components/ui";
import { Callout } from "@/components/ui/Callout";
import { TRACKERS } from "@/constants/Trackers";
import { SectionContent } from "@/features/Patient/MedicalRecords/components/SectionContent";
import {
  SectionHeader,
  SectionSubheader,
} from "@/features/Patient/MedicalRecords/components/SectionHeader";
import { useGetPatient } from "@/features/Patient/network/usePatient";
import { trackEvent } from "@/lib/tracking";
import { useGetGenericObservations } from "@/network/useGenericObservations";
import {
  QuestionnaireCategory,
  useGetQuestionnaire,
} from "@/network/useQuestionnaires";
import type { ObservationGroupItem } from "@/types/observations";
import {
  LifestyleQuestionnaireTypes,
  type MentalHealthQuestionnaireTypes,
  type QuestionnaireResponseData,
} from "@/types/questionnaires";
import { constants } from "@curai/js-fhir-tools";
import { useParams } from "@tanstack/react-router";
import { BicepsFlexed, Handshake } from "lucide-react";
import { Fragment, useState } from "react";
import { QuestionnaireDetailDialog } from "../HealthMaintenance/components/QuestionnaireDetailDialog/QuestionnaireDetailDialog";
import { formatQuestionnaireScore } from "../HealthMaintenance/utils";
import { GenericObservationEditor } from "../components/GenericObservationEditor";
import { MaritalStatusItem } from "./MaritalStatusItem";
import {
  SocialHistoryCommunitySkeleton,
  SocialHistoryLifestyleSkeleton,
} from "./SocialHistorySkeleton";

const LIFESTYLE_GROUP_IDENTIFIER = "lifestyle";
const COMMUNITY_GROUP_IDENTIFIER = "community";

const TRACKER_UUID_BY_QUESTIONNAIRE_ID: Record<
  LifestyleQuestionnaireTypes,
  string | null
> = {
  [LifestyleQuestionnaireTypes.GLTEQ]:
    TRACKERS.VIEW_PHYSICAL_ACTIVITY_QUESTIONNAIRE_RESPONSES_FROM_SOCIAL_HISTORY,
  [LifestyleQuestionnaireTypes.AUDIT_C]:
    TRACKERS.VIEW_ALCOHOL_QUESTIONNAIRE_RESPONSES_IN_SOCIAL_HISTORY,
  [LifestyleQuestionnaireTypes.FTND]:
    TRACKERS.VIEW_TOBACCO_QUESTIONNAIRE_RESPONSES_IN_SOCIAL_HISTORY,
  [LifestyleQuestionnaireTypes.REAP_S]:
    TRACKERS.VIEW_DIET_QUESTIONNAIRE_RESPONSES_IN_SOCIAL_HISTORY,
  [LifestyleQuestionnaireTypes.PSQI]: null,
};

const getQuestionnaireResponseFromResponseData = (
  questionnaireId: LifestyleQuestionnaireTypes,
  lifestyleQuestionnaireData: QuestionnaireResponseData[],
): QuestionnaireResponseData | null => {
  return (
    lifestyleQuestionnaireData?.find(
      (q) => q.questionnaireId === questionnaireId,
    ) ?? null
  );
};

const getFriendlyQuestionnaireName = (
  questionnaire: QuestionnaireResponseData,
): string => {
  return questionnaire.questionnaireName.replace("_", "-");
};

const getQuestionnaireIdAssociatedToObservationGroupItem = (
  item: ObservationGroupItem,
): LifestyleQuestionnaireTypes | null => {
  if (
    item.observationCodeSystem === constants.SNOMED_SYSTEM_URL &&
    item.observationCode === "230125005"
  ) {
    return LifestyleQuestionnaireTypes.REAP_S;
  }
  if (
    item.observationCodeSystem === constants.SNOMED_SYSTEM_URL &&
    item.observationCode === "61686008"
  ) {
    return LifestyleQuestionnaireTypes.GLTEQ;
  }
  if (
    item.observationCodeSystem === constants.SNOMED_SYSTEM_URL &&
    item.observationCode === "229819007"
  ) {
    return LifestyleQuestionnaireTypes.FTND;
  }
  if (
    item.observationCodeSystem === constants.SNOMED_SYSTEM_URL &&
    item.observationCode === "897148007"
  ) {
    return LifestyleQuestionnaireTypes.AUDIT_C;
  }

  return null;
};

const QuestionaireResponseScore = ({
  questionnaireId,
  lifestyleQuestionnaireData,
  onClick,
}: {
  questionnaireId: LifestyleQuestionnaireTypes;
  lifestyleQuestionnaireData: QuestionnaireResponseData[];
  onClick: () => void;
}) => {
  const questionnaire = getQuestionnaireResponseFromResponseData(
    questionnaireId,
    lifestyleQuestionnaireData,
  );
  if (!questionnaire) {
    return null;
  }
  // we have questionnaire info about this questionnaire and its association to an observation, but the
  // patient has not yet submitted any questionnaire response
  if (!questionnaire.questionnaireResponse) {
    return (
      <div className="px-3 pb-2 text-xs text-gray-500">
        <span>{`${getFriendlyQuestionnaireName(questionnaire)}: --`}</span>
      </div>
    );
  }

  return (
    <div className="px-3 pb-2 text-xs text-gray-500">
      <span>{`${getFriendlyQuestionnaireName(questionnaire)}: `}</span>
      <Button
        variant="link"
        size="sm"
        className="font-normal ml-1 underline decoration-1 underline-offset-2"
        onClick={onClick}
      >
        <Tooltip tooltipContentText="See Results" side="bottom">
          {formatQuestionnaireScore(
            questionnaire.responseScore,
            questionnaire.maxScore,
          )}
        </Tooltip>
      </Button>
    </div>
  );
};

export const SocialHistory = () => {
  const { patientId } = useParams({ strict: false });
  const {
    data: patient,
    isLoading: isLoadingPatient,
    isError: isErrorPatient,
    refetch: refetchPatient,
  } = useGetPatient(patientId);

  const {
    data: lifestyleGroupItems,
    isLoading: isLoadingLifestyleGroupItems,
    isError: isErrorLifestyleGroupItems,
    refetch: refetchLifestyleGroupItems,
  } = useGetGenericObservations(patientId, LIFESTYLE_GROUP_IDENTIFIER);

  const {
    data: communityGroupItems,
    isLoading: isLoadingCommunityGroupItems,
    isError: isErrorCommunityGroupItems,
    refetch: refetchCommunityGroupItems,
  } = useGetGenericObservations(patientId, COMMUNITY_GROUP_IDENTIFIER);

  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<
    LifestyleQuestionnaireTypes | MentalHealthQuestionnaireTypes | null
  >(null);

  const {
    data: lifestyleQuestionnaireData,
    isLoading: isLoadingLifestyleQuestionnaireData,
    isError: isErrorLifestyleQuestionnaireData,
    refetch: refetchLifestyleQuestionnaireData,
  } = useGetQuestionnaire(patientId, QuestionnaireCategory.LIFESTYLE);

  const [openQuestionnaireDetailDialog, setOpenQuestionnaireDetailDialog] =
    useState(false);

  return (
    <div className="w-full h-full flex flex-col">
      <SectionHeader title="Social History" />
      <SectionContent>
        <SectionSubheader title="Community" iconComponent={Handshake} />
        {(isLoadingPatient || isLoadingCommunityGroupItems) && (
          <SocialHistoryCommunitySkeleton />
        )}
        {(isErrorPatient || isErrorCommunityGroupItems) && (
          <div className="flex flex-col gap-2 py-2 w-full">
            <Callout
              className="w-full cursor-pointer"
              variant="danger"
              title="Unable to load community data!"
              description="Please click here to try again."
              onClick={() => {
                refetchCommunityGroupItems();
                refetchPatient();
              }}
            />
          </div>
        )}
        {patient && communityGroupItems && (
          <div className="flex flex-wrap w-full">
            <div className="w-full">
              <MaritalStatusItem patient={patient} />
              <Separator />
            </div>
            {communityGroupItems.items.map(
              (item: ObservationGroupItem, index: number) => (
                <Fragment key={item.observationCode}>
                  <div className="w-1/2">
                    <GenericObservationEditor
                      item={item}
                      itemCategory="social-history"
                      group={COMMUNITY_GROUP_IDENTIFIER}
                    />
                  </div>
                  {(index + 1) % 2 === 0 && <Separator className="w-full" />}
                </Fragment>
              ),
            )}
          </div>
        )}
        <SectionSubheader title="Lifestyle" iconComponent={BicepsFlexed} />
        {isLoadingLifestyleGroupItems && <SocialHistoryLifestyleSkeleton />}
        {isErrorLifestyleGroupItems && (
          <div className="flex flex-col gap-2 py-2 w-full">
            <Callout
              className="w-full cursor-pointer"
              variant="danger"
              title="Unable to load lifestyle data!"
              description="Please click here to try again."
              onClick={() => {
                refetchLifestyleGroupItems();
                refetchPatient();
              }}
            />
          </div>
        )}
        {isErrorLifestyleQuestionnaireData && (
          <div className="flex flex-col gap-2 py-2 w-full">
            <Callout
              className="w-full cursor-pointer"
              variant="danger"
              title="Unable to load lifestyle questionnaire data!"
              description="Please click here to try again."
              onClick={() => {
                refetchLifestyleQuestionnaireData();
              }}
            />
          </div>
        )}
        {lifestyleGroupItems && (
          <div className="flex flex-wrap w-full">
            {lifestyleGroupItems.items.map(
              (item: ObservationGroupItem, index: number) => {
                const questionnaireId =
                  getQuestionnaireIdAssociatedToObservationGroupItem(item);
                return (
                  <Fragment key={item.observationCode}>
                    <div className="w-1/2">
                      <GenericObservationEditor
                        item={item}
                        itemCategory="social-history"
                        group={LIFESTYLE_GROUP_IDENTIFIER}
                      />
                      {questionnaireId &&
                        isLoadingLifestyleQuestionnaireData && (
                          <Skeleton className="h-4 w-1/2 mb-2" />
                        )}
                      {questionnaireId && lifestyleQuestionnaireData ? (
                        <QuestionaireResponseScore
                          questionnaireId={questionnaireId}
                          lifestyleQuestionnaireData={
                            lifestyleQuestionnaireData
                          }
                          onClick={() => {
                            const trackerUuid =
                              TRACKER_UUID_BY_QUESTIONNAIRE_ID[questionnaireId];
                            trackerUuid && trackEvent(trackerUuid);
                            setSelectedQuestionnaireId(questionnaireId);
                            setOpenQuestionnaireDetailDialog(true);
                          }}
                        />
                      ) : null}
                    </div>
                    {(index + 1) % 2 === 0 && <Separator className="w-full" />}
                  </Fragment>
                );
              },
            )}
          </div>
        )}
        {selectedQuestionnaireId && patientId && (
          <QuestionnaireDetailDialog
            selectedQuestionnaireType={selectedQuestionnaireId}
            allQuestionnaires={lifestyleQuestionnaireData ?? []}
            patientId={patientId}
            open={openQuestionnaireDetailDialog}
            onOpenChange={setOpenQuestionnaireDetailDialog}
          />
        )}
      </SectionContent>
    </div>
  );
};
